class SizePicker extends HTMLElement {
  connectedCallback() {
    this._ready = false;
    this.trigger = this.querySelector('.size-picker__trigger');
    this.triggerLabel = this.querySelector('.size-picker__trigger-label');
    this.dropdown = this.querySelector('.size-picker__dropdown');
    this.hiddenInput = this.querySelector('input[data-size-picker-input]');

    if (!this.trigger || !this.hiddenInput || !this.dropdown) return;

    this.currentSystem = this._getDefaultSystem();
    // Keep a single source of truth for other UI (e.g. sticky cart labels).
    localStorage.setItem('preferred-size-system', this.currentSystem);
    const detectedCountry = this._detectCountry();
    if (detectedCountry) {
      localStorage.setItem('preferred-size-system-country', detectedCountry);
    }
    this._applySystem(this.currentSystem);
    this.triggerLabel.textContent = this._getDisplayValue(this.hiddenInput.value);
    this._ready = true;

    this.trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    });

    this.querySelectorAll('.size-picker__option').forEach(opt => {
      opt.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectOption(opt);
      });
    });

    this.querySelectorAll('.size-picker__system-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.switchSystem(btn.dataset.system);
      });
    });

    this._outsideClickHandler = (e) => {
      if (this.dropdown.hidden) return;
      if (this.contains(e.target)) return;
      if (e.target.closest('modal-dialog') || e.target.closest('.product-popup-modal')) return;
      this.close();
    };

    this._escHandler = (e) => {
      if (e.key === 'Escape' && !this.dropdown.hidden) this.close();
    };

    document.addEventListener('click', this._outsideClickHandler);
    document.addEventListener('keydown', this._escHandler);
  }

  disconnectedCallback() {
    if (this._outsideClickHandler) {
      document.removeEventListener('click', this._outsideClickHandler);
    }
    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler);
    }
  }

  toggle() {
    if (!this.dropdown) return;
    if (this.dropdown.hidden) this.open();
    else this.close();
  }

  open() {
    if (!this.dropdown) return;
    document.querySelectorAll('size-picker').forEach(p => {
      if (p !== this && p._ready && p.dropdown && !p.dropdown.hidden) p.close();
    });
    this.dropdown.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.dropdown || this.dropdown.hidden) return;
    this.dropdown.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  selectOption(optionEl) {
    if (optionEl.hasAttribute('data-disabled')) return;

    const euValue = optionEl.dataset.value;

    this.querySelectorAll('.size-picker__option').forEach(o => o.removeAttribute('aria-selected'));
    optionEl.setAttribute('aria-selected', 'true');

    this.hiddenInput.value = euValue;
    this.triggerLabel.textContent = this._getDisplayValue(euValue);

    this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.close();
  }

  setValue(euValue) {
    const opt = this.querySelector(`.size-picker__option[data-value="${euValue}"]`);
    if (opt) {
      this.querySelectorAll('.size-picker__option').forEach(o => o.removeAttribute('aria-selected'));
      opt.setAttribute('aria-selected', 'true');
      this.hiddenInput.value = euValue;
      if (this.triggerLabel) this.triggerLabel.textContent = this._getDisplayValue(euValue);
    }
  }

  switchSystem(system) {
    this.currentSystem = system;
    localStorage.setItem('preferred-size-system', system);
    const detectedCountry = this._detectCountry();
    if (detectedCountry) {
      localStorage.setItem('preferred-size-system-country', detectedCountry);
    }
    this._applySystem(system);

    if (this.hiddenInput && this.triggerLabel) {
      this.triggerLabel.textContent = this._getDisplayValue(this.hiddenInput.value);
    }

    document.querySelectorAll('size-picker').forEach(picker => {
      if (picker !== this && picker._ready) {
        picker.currentSystem = system;
        picker._applySystem(system);
        if (picker.hiddenInput && picker.triggerLabel) {
          picker.triggerLabel.textContent = picker._getDisplayValue(picker.hiddenInput.value);
        }
      }
    });

    document.querySelectorAll('[data-sticky-size-select]').forEach(sel => {
      Array.from(sel.options).forEach(opt => {
        const eu = opt.getAttribute('data-eu-value');
        if (!eu) return;
        if (system === 'eu') { opt.textContent = eu; return; }
        const m = SizePicker.SIZE_MAP[eu];
        opt.textContent = (m && m[system]) ? m[system] : eu;
      });
    });
  }

  _applySystem(system) {
    this.querySelectorAll('.size-picker__system-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.system === system);
    });

    this.querySelectorAll('.size-picker__option').forEach(opt => {
      const label = opt.querySelector('.size-picker__option-label');
      if (label) label.textContent = this._getDisplayValue(opt.dataset.value);
    });
  }

  _getDisplayValue(euValue) {
    if (this.currentSystem === 'eu') return euValue;
    const mapping = SizePicker.SIZE_MAP[euValue];
    if (!mapping) return euValue;
    return mapping[this.currentSystem] || euValue;
  }

  _getDefaultSystem() {
    const country = this._detectCountry();
    const saved = localStorage.getItem('preferred-size-system');

    // Only reuse saved preference when it was saved for same country context.
    const savedCountry = localStorage.getItem('preferred-size-system-country');
    if (saved && savedCountry && country && savedCountry === country) return saved;

    if (country === 'US' || country === 'CA') return 'us';
    if (country === 'GB' || country === 'IE') return 'uk';
    if (saved) return saved;
    return 'eu';
  }

  _detectCountry() {
    const countryInput = document.querySelector('input[name="country_code"]');
    const candidates = [
      window.Shopify && window.Shopify.country,
      countryInput ? countryInput.value : ''
    ];

    for (let i = 0; i < candidates.length; i++) {
      const value = candidates[i];
      if (!value) continue;
      const normalized = String(value).trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(normalized)) return normalized;
    }

    return '';
  }

  setDisabled(euValue, disabled) {
    const opt = this.querySelector(`.size-picker__option[data-value="${euValue}"]`);
    if (opt) {
      if (disabled) opt.setAttribute('data-disabled', '');
      else opt.removeAttribute('data-disabled');
    }
  }

  setAllDisabled() {
    this.querySelectorAll('.size-picker__option').forEach(opt => opt.setAttribute('data-disabled', ''));
  }
}
SizePicker.SIZE_MAP = {
  '36': { us: '4', uk: '3.5' },
  '37': { us: '5', uk: '4' },
  '38': { us: '6', uk: '5' },
  '39': { us: '7', uk: '6' },
  '40': { us: '7.5', uk: '6.5' },
  '41': { us: '8', uk: '7' },
  '42': { us: '9', uk: '8' },
  '43': { us: '10', uk: '9' },
  '44': { us: '10.5', uk: '9.5' },
  '45': { us: '11', uk: '10' },
  '46': { us: '12', uk: '11' },
  '47': { us: '13', uk: '12' },
  '48': { us: '14', uk: '13' }
};

if (!customElements.get('size-picker')) {
  customElements.define('size-picker', SizePicker);
}
